import { createSlice, createListenerMiddleware } from '@reduxjs/toolkit';
import type { Draft, PayloadAction } from '@reduxjs/toolkit';

import { SliceSelectors } from 'state/meter-type/meter-type-slice';

import type { TMeter } from 'api/models/TMeter';
import type { NormalizedError } from 'errors/types';
import type { RootState, AppStartListening } from 'state/store';
import type { TAddress } from 'api/models/TAddress';

export type TMpxn = Record<
  string,
  { meter?: TMeter; isLoading: boolean; errors: NormalizedError[] }
>;

export type TMpxnEntry = TMpxn[string];

export type MeterPointValidationState = {
  validationRequired: {
    electricity: boolean | null;
    gas: boolean | null;
  };
  userMeterpoints: {
    MPRN: string | null;
    MPAN: string | null;
  };
  data: TMeter[];
  mpxn: TMpxn;
  isLoading: boolean;
  errors: NormalizedError[] | null;
};

function upsertMeter(
  state: Draft<MeterPointValidationState>,
  meter: TMeter | undefined
) {
  if (!meter) return;
  const idx = state.data.findIndex(
    ({ fuelType }) => fuelType === meter.fuelType
  );
  idx >= 0 ? (state.data[idx] = meter) : state.data.push(meter);
}

function matchesFuelType(e: NormalizedError, fuelType: string) {
  return e.keyword === fuelType || e.fuelType === fuelType;
}

function mergeErrors(
  state: Draft<MeterPointValidationState>,
  incoming: NormalizedError[],
  fuelType: string
) {
  const existing = (state.errors ?? []).filter(
    (e) => !matchesFuelType(e, fuelType)
  );
  const filtered = incoming.filter((e) => matchesFuelType(e, fuelType));
  state.errors = [...existing, ...filtered];
}

export const initialState: MeterPointValidationState = {
  validationRequired: {
    electricity: null,
    gas: null
  },
  userMeterpoints: {
    MPRN: null,
    MPAN: null
  },
  mpxn: {},
  data: [],
  isLoading: false,
  errors: null
};

export const slice = createSlice({
  name: 'meterpointValidation',
  initialState,
  reducers: {
    setInitialState: (state) => {
      Object.entries(initialState).forEach(
        ([key, value]) => (state[key] = value)
      );
    },
    setMPRN: (state, { payload }) => {
      state.userMeterpoints.MPRN = payload;
    },
    setMPAN: (state, { payload }) => {
      state.userMeterpoints.MPAN = payload;
    },
    removeMPRN: (state) => {
      state.userMeterpoints.MPRN = initialState.userMeterpoints.MPRN;
      state.data = state.data.filter(({ fuelType }) => fuelType !== 'Gas');
    },
    removeMPAN: (state) => {
      state.userMeterpoints.MPAN = initialState.userMeterpoints.MPAN;
      state.data = state.data.filter(
        ({ fuelType }) => fuelType !== 'Electricity'
      );
    },
    setValidationRequired: (state, { payload }) => {
      state.validationRequired = payload;
    },
    resetData: (state) => {
      state.data = initialState.data;
    },
    resetErrors: (state) => {
      state.errors = initialState.errors;
    },
    setIsLoading: (state, { payload }) => {
      const { mpan, mprn } = payload.params ?? {};

      const mpanOnly = mpan && !mprn;
      const mprnOnly = mprn && !mpan;

      state.isLoading = true;

      switch (true) {
        case mpanOnly:
          mergeErrors(state, [], 'Electricity');
          break;
        case mprnOnly:
          mergeErrors(state, [], 'Gas');
          break;
        default:
          state.errors = initialState.errors;
      }
    },
    setIsLoadingMpxn: (state, { payload }) => {
      const { mpan, mprn } = payload.params;

      if (mpan) {
        state.mpxn[mpan] = {
          isLoading: true,
          errors: payload.errors
        };
      }

      if (mprn) {
        state.mpxn[mprn] = {
          isLoading: true,
          errors: payload.errors
        };
      }
    },
    setSuccessMpxn: (state, { payload }) => {
      const { mpan, mprn } = payload.params;

      if (mpan) {
        state.mpxn[mpan] = {
          meter: payload.meterpoints.find(
            ({ fuelType }) => fuelType === 'Electricity'
          ),
          isLoading: false,
          errors: payload.errors?.filter((e) =>
            matchesFuelType(e, 'Electricity')
          )
        };
      }

      if (mprn) {
        state.mpxn[mprn] = {
          meter: payload.meterpoints.find(({ fuelType }) => fuelType === 'Gas'),
          isLoading: false,
          errors: payload.errors?.filter((e) => matchesFuelType(e, 'Gas'))
        };
      }
    },
    setErrorMpxn: (state, { payload }) => {
      const { mpan, mprn, errors } = payload;

      if (mpan) {
        state.mpxn[mpan] = {
          isLoading: false,
          errors: errors?.filter((e) => matchesFuelType(e, 'Electricity'))
        };
      }

      if (mprn) {
        state.mpxn[mprn] = {
          isLoading: false,
          errors: errors?.filter((e) => matchesFuelType(e, 'Gas'))
        };
      }
    },
    removeMpxn: (state, { payload }: PayloadAction<string | undefined>) => {
      if (payload) {
        delete state.mpxn[payload];
      } else {
        state.mpxn = initialState.mpxn;
      }
    },
    syncData: (state, { payload }) => {
      state.data = payload.data;
      state.errors = payload.errors;
    },
    setSuccess: (state, { payload }) => {
      const { mpan, mprn } = payload.params ?? {};
      const mpanOnly = mpan && !mprn;
      const mprnOnly = mprn && !mpan;

      state.isLoading = false;
      switch (true) {
        case mpanOnly:
          upsertMeter(
            state,
            payload.meterpoints.find(
              ({ fuelType }) => fuelType === 'Electricity'
            )
          );
          mergeErrors(state, payload.errors, 'Electricity');
          break;
        case mprnOnly:
          upsertMeter(
            state,
            payload.meterpoints.find(({ fuelType }) => fuelType === 'Gas')
          );
          mergeErrors(state, payload.errors, 'Gas');
          break;
        default:
          state.data = payload.meterpoints;
          state.errors = payload.errors;
      }
    },
    setError: (state, { payload }) => {
      state.isLoading = false;
      state.errors = payload;
    }
  }
});

export const {
  setInitialState,
  setMPRN,
  setMPAN,
  removeMPAN,
  removeMPRN,
  setValidationRequired,
  setIsLoading,
  setIsLoadingMpxn,
  setSuccess,
  setSuccessMpxn,
  setErrorMpxn,
  removeMpxn,
  setError,
  resetData,
  resetErrors
} = slice.actions;

const defaultSelectors = new SliceSelectors(slice.name);

const {
  selectSlice,
  selectElectricityMeter,
  selectGasMeter: selectGasMeterFromData
} = defaultSelectors;

export const selectMpxn = (state: RootState) =>
  selectSlice(state).mpxn as MeterPointValidationState['mpxn'];

export const selectElectricityMpxn = (state: RootState) =>
  Object.entries(selectMpxn(state)).filter(
    ([, data]) =>
      data.meter?.fuelType === 'Electricity' ||
      data.errors?.some((error) => error?.fuelType === 'Electricity')
  ) as [string, TMpxnEntry][];

export const selectGasMpxn = (state: RootState) =>
  Object.entries(selectMpxn(state)).filter(
    ([, data]) =>
      data.meter?.fuelType === 'Gas' ||
      data.errors?.some((error) => error?.fuelType === 'Gas')
  );

export const selectElectricityMpxnMeters = (state: RootState): TMeter[] =>
  selectElectricityMpxn(state)
    .map(([, { meter }]) => meter)
    .filter((m): m is TMeter => !!m);

export const selectGasMeter = (state: RootState) =>
  selectGasMpxn(state).map(([, { meter }]) => meter)[0];

export const selectUserMeterpoints = (
  state: RootState
): MeterPointValidationState['userMeterpoints'] =>
  selectSlice(state).userMeterpoints;

export const selectValidationRequired = (
  state: RootState
): MeterPointValidationState['validationRequired'] =>
  selectSlice(state).validationRequired;

export const selectMeterAddressesDeprecated = (
  state: RootState
): {
  electricity?: TAddress;
  gas?: TAddress;
} => {
  const { electricity, gas } = selectValidationRequired(state);
  const electricityMeter = selectElectricityMeter(state);
  const gasMeter = selectGasMeterFromData(state);

  return {
    ...(electricity && {
      electricity: electricityMeter ? electricityMeter.address : undefined
    }),
    ...(gas && { gas: gasMeter ? gasMeter.address : undefined })
  };
};

export const selectValidationErrorsDeprecated = (state: RootState) => {
  const errors = defaultSelectors.selectErrors(state) || [];

  return errors.filter(
    (error) => error && error.keyword?.toLowerCase() === 'validation'
  );
};

export const selectors = {
  ...defaultSelectors,
  selectElectricityMpxnMeters,
  selectMpxn,
  selectGasMpxn,
  selectElectricityMpxn,
  selectUserMeterpoints,
  selectValidationRequired,
  selectValidationErrorsDeprecated
};

// middleware
export const meterValidationSyncMpxnDataMiddleware = createListenerMiddleware();

const meterValidationSyncMpxnDataMiddlewareStartListening = meterValidationSyncMpxnDataMiddleware.startListening as AppStartListening;

meterValidationSyncMpxnDataMiddlewareStartListening({
  predicate: (action) => {
    const isSetSuccessMpxn = action.type === setSuccessMpxn.type;
    const isRemoveMpxn = action.type === removeMpxn.type;

    return isSetSuccessMpxn || isRemoveMpxn;
  },
  effect: (action, listenerApi) => {
    const state = listenerApi.getState();
    const elecMeters = selectElectricityMpxnMeters(state);
    const gasMeter = selectGasMeter(state);
    const data = [...elecMeters, gasMeter].filter(Boolean);
    const errors = Object.values(selectMpxn(state)).flatMap(
      ({ errors }) => errors
    );

    listenerApi.dispatch(
      slice.actions.syncData({
        data,
        errors
      })
    );
  }
});

export default slice.reducer;
